/**
 * Helper to diagnose network connectivity issues
 */
export const checkConnectivity = async (sipConfig) => {
	console.group('NETWORK DIAGNOSTICS');

	try {
		// Check basic internet connectivity
		console.log('Checking internet connectivity...');
		const internetConnected = navigator.onLine;
		console.log(`Internet connected: ${internetConnected}`);

		if (!internetConnected) {
			console.groupEnd();
			return {
				success: false,
				issues: ['No internet connection'],
				recommendation: 'Please check your internet connection and try again.'
			};
		}

		// Check WebSocket connectivity to SIP server
		console.log('Checking SIP server connectivity...');

		let wsStatus = 'unknown';
		const wsUrl = sipConfig?.uri;

		if (!wsUrl) {
			console.log('No WebSocket URL provided');
			console.groupEnd();
			return {
				success: false,
				issues: ['Invalid SIP server configuration'],
				recommendation: 'The SIP server configuration is missing or invalid.'
			};
		}

		// Create a test WebSocket connection
		const testSocket = new WebSocket(wsUrl);

		// Promise to handle WS connection test
		const wsTest = new Promise((resolve) => {
			testSocket.onopen = () => {
				wsStatus = 'connected';
				resolve(true);
				testSocket.close();
			};

			testSocket.onerror = (error) => {
				wsStatus = 'error';
				console.error('WebSocket connection error:', error);
				resolve(false);
			};

			// Add timeout for the test
			setTimeout(() => {
				if (wsStatus === 'unknown') {
					wsStatus = 'timeout';
					resolve(false);
					testSocket.close();
				}
			}, 5000);
		});

		const wsConnected = await wsTest;
		console.log(`WebSocket connectivity: ${wsStatus}`);

		if (!wsConnected) {
			console.groupEnd();
			let recommendation = 'Unable to connect to the SIP server.';

			if (wsStatus === 'error') {
				recommendation += ' Your firewall may be blocking the connection.';
			} else if (wsStatus === 'timeout') {
				recommendation += ' The server may be down or unreachable.';
			}

			return {
				success: false,
				issues: [`SIP server connection failed: ${wsStatus}`],
				recommendation
			};
		}

		// Check for media device permissions
		console.log('Checking media device permissions...');
		let mediaPermissions = 'untested';

		try {
			// Try to get audio permissions
			const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

			// Check if we got audio tracks
			if (mediaStream.getAudioTracks().length > 0) {
				mediaPermissions = 'granted';

				// Remember to release the stream
				mediaStream.getTracks().forEach(track => track.stop());
			} else {
				mediaPermissions = 'no-audio';
			}
		} catch (error) {
			mediaPermissions = 'denied';
			console.error('Media permissions error:', error);
		}

		console.log(`Media permissions: ${mediaPermissions}`);

		if (mediaPermissions !== 'granted') {
			console.groupEnd();
			return {
				success: false,
				issues: ['Microphone access denied or unavailable'],
				recommendation: 'Please allow microphone access in your browser settings to make calls.'
			};
		}

		// If we got here, basic connectivity checks passed
		console.log('Basic connectivity checks passed');
		console.groupEnd();

		return {
			success: true,
			issues: [],
			recommendation: null
		};
	} catch (error) {
		console.error('Error in connectivity check:', error);
		console.groupEnd();

		return {
			success: false,
			issues: ['Unexpected error during connectivity check'],
			recommendation: `An error occurred while checking connectivity: ${error.message}`
		};
	}
};


